<script setup lang="ts">
import { computed, ref } from 'vue'

import { storeToRefs } from 'pinia'

import CriteriaSummary from '@/components/criteria/CriteriaSummary.vue'
import DumbSummaryRow from '@/components/dumb/DumbSummaryRow.vue'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import { useTimeout } from '@/composables/useTimeout'
import { useFlowStore } from '@/stores/flowStore'
import type { CriterionSummaryItem } from '@/types/CriteriaTypes'

const flowStore = useFlowStore()
const { flow } = storeToRefs(flowStore)

const { set, clear } = useTimeout()

const isPopoverOpen = ref<boolean>(false)

const criteriaSummary = computed<CriterionSummaryItem[]>(() => {
  if (!flow.value) {
    return []
  }

  return [...flow.value.criterionValues]
    .sort((a, b) => a.criterion.index - b.criterion.index)
    .map(criterionValue => ({
      criterionUuid: criterionValue.criterionUuid,
      criterionName: criterionValue.criterion.name,
      valueName: criterionValue.name,
    }))
})

const openPopover = (): void => {
  clear()
  isPopoverOpen.value = true
}

const scheduleClosePopover = (): void => {
  set(() => {
    isPopoverOpen.value = false
  }, 120)
}
</script>

<template>
  <Popover v-model:open="isPopoverOpen">
    <PopoverTrigger as-child>
      <button
        type="button"
        class="max-w-xl cursor-pointer truncate border-0 bg-transparent p-0 text-left text-inherit outline-none focus:outline-none focus-visible:outline-none"
        @mouseenter="openPopover"
        @mouseleave="scheduleClosePopover"
      >
        {{ flow?.name }}
      </button>
    </PopoverTrigger>
    <PopoverContent
      side="bottom"
      :side-offset="2"
      class="w-auto max-w-md min-w-72 bg-white p-0"
      @mouseenter="openPopover"
      @mouseleave="scheduleClosePopover"
    >
      <div v-if="flow" class="flex flex-col">
        <CriteriaSummary
          :items="criteriaSummary"
          class="rounded-none border-0"
        />
        <Separator />
        <dl class="divide-y">
          <DumbSummaryRow
            :label="$t('flow.manage.version')"
            :value="flow.version"
          />
          <DumbSummaryRow :label="$t('flow.manage.name')" :value="flow.name" />
          <DumbSummaryRow :label="$t('flow.manage.code')" :value="flow.code" />
          <DumbSummaryRow
            :label="$t('flow.manage.createdBy')"
            :value="`${flow.createdBy.firstName} ${flow.createdBy.lastName}`"
          />
        </dl>
      </div>
    </PopoverContent>
  </Popover>
</template>
